/**
 * Fan a header-level procedure error out onto the line items it belongs to.
 *
 * gRPC reports requested/previous procedure errors at header level
 * (kopf.angemeldetes / kopf.vorangegangenes), but the fields live on every
 * POSITION. GRPC_FIELD_PATH_ALIASES therefore maps those names to a sentinel —
 * `__fanout_beantragtesVerfahren__` — which has to be expanded into one error
 * per position before the errors are rendered.
 *
 * That expansion existed in handleSendData ONLY. The auto-validation path had
 * none, so the sentinel reached the error list verbatim and was displayed as a
 * field called "__fanout_beantragtesVerfahren__" in the "no matching field on
 * this form" panel: unactionable, and nowhere near the line items that needed
 * a procedure code.
 *
 * Exactly the fault GRPC_FIELD_PATH_ALIASES itself was created to end — one
 * behaviour implemented twice, drifting. One function, both callers.
 */
export interface FanoutField {
  sentinel: string;
  fieldKey: string;
  label: string;
}

export const PROCEDURE_FANOUT_FIELDS: FanoutField[] = [
  { sentinel: '__fanout_beantragtesVerfahren__', fieldKey: 'beantragtesVerfahren', label: 'Requested Procedure' },
  { sentinel: '__fanout_vorhergehendesVerfahren__', fieldKey: 'vorhergehendesVerfahren', label: 'Previous Procedure' },
];

/** True when a position has no usable procedure code. "00" counts as unset. */
const isUnset = (value: any): boolean =>
  value === undefined || value === null || value === '' || value === '00';

/**
 * Replace every fanout sentinel in `fieldErrors` with one error per position.
 * Mutates and returns `fieldErrors`. Returns it unchanged when no sentinel is
 * present, so it is safe to call on every validation result.
 */
export function expandProcedureFanout(
  fieldErrors: Record<string, string>,
  positions: any[] | null | undefined,
): Record<string, string> {
  const items = Array.isArray(positions) ? positions : [];

  PROCEDURE_FANOUT_FIELDS.forEach(({ sentinel, fieldKey }) => {
    const message = fieldErrors[sentinel];
    if (!message) return;
    delete fieldErrors[sentinel];

    if (items.length === 0) {
      // No position data yet — the first row is the only place to put it.
      fieldErrors[`position[0].${fieldKey}`] = message;
      return;
    }

    const missing = items
      .map((pos, idx) => ({ idx, empty: isUnset(pos?.[fieldKey]) }))
      .filter(entry => entry.empty);

    // Prefer the rows that actually lack the code; if every row has one, the
    // requirement is about all of them, so show it on all.
    const targets = missing.length ? missing.map(e => e.idx)
                                   : items.map((_, idx) => idx);
    targets.forEach(idx => {
      fieldErrors[`position[${idx}].${fieldKey}`] = message;
    });
  });

  return fieldErrors;
}

/** Any sentinel still present means an expansion was missed. For tests. */
export function hasUnexpandedSentinel(fieldErrors: Record<string, string>): boolean {
  return PROCEDURE_FANOUT_FIELDS.some(f => f.sentinel in fieldErrors);
}
